import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { FormControl } from '@components/common/FormControl/FormControl';
import { FormSelect } from '@components/common/FormSelect/FormSelect';
import { InputAdornment } from '@components/common/InputAdornment';
import { ApplicationFormPageEnum } from '../types';
import { residencyQuestions } from './questions';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { YesNoEnum } from '../HouseholdOutgoings/types';
import { IResidencyForm } from './types';
import { getCurrentRoute } from '@utils/location';
import { useMutation } from '@tanstack/react-query';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { useEffect } from 'react';
import { parseApplicationInProgress } from '../helpers';
import { FormNumberField } from '@components/common/FormNumberField/FormNumberField';
import { countries } from './countries';

export const Residency = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeApplicant = parseInt(searchParams.get('activeApplicant') || '');
  const isJoined = searchParams.get('isJoined') === 'true';
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IResidencyForm>({
    defaultValues: {
      hasPermanentResidency: '',
    },
  });
  const formState = form.watch();

  useEffect(() => {
    const currentNationality = formState.nationality;
    if (formState.hasPermanentResidency === YesNoEnum.YES) form.reset({ hasPermanentResidency: YesNoEnum.YES });
    form.setValue('nationality', currentNationality);
  }, [formState.hasPermanentResidency]);

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IResidencyForm) => {
    const filteredFormData = Object.entries(formData).filter(([, value]) => value);
    const questions = filteredFormData.map(([key, answer]) => ({
      question: intl.formatMessage({ id: residencyQuestions[key].labelKey }),
      key,
      answer,
    }));

    // Have to use a fake value to put another 'British' at the top of the list -
    // if the user selected the top one we set the answer to the normal value
    if (questions[1].answer === 'AAABritish') questions[1].answer = 'British';

    await patchApplicationInProgress({ questions, currentRoute, applicationId });
    let nextPage = ApplicationFormPageEnum.ADDRESS_HISTORY;

    if (isJoined && activeApplicant === 1) {
      nextPage = ApplicationFormPageEnum.FULL_NAME;
      searchParams.set('activeApplicant', `${activeApplicant + 1}`);
    } else {
      searchParams.set('activeApplicant', '1');
    }

    searchParams.set('section', nextPage);
    setSearchParams(searchParams);
  };

  const noPermanentResidencyBoolean = formState.hasPermanentResidency === YesNoEnum.NO;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <section style={{ marginBottom: '3rem' }}>
          <FormattedMessage id={residencyQuestions.nationality.labelKey} tagName={'h4'} />
          <FormSelect
            rules={{ required: requiredFieldErrorMessage }}
            name={'nationality'}
            options={countries}
            selectInputProps={{ placeholder: intl.formatMessage({ id: 'placeholders.selectNationality' }) }}
          />
        </section>

        <section>
          <FormattedMessage id={residencyQuestions.hasPermanentResidency.labelKey} tagName={'h4'} />
          <FormattedMessage id={residencyQuestions.hasPermanentResidency.explanationKey} tagName={'p'} />

          <FormControl
            rules={{ required: requiredFieldErrorMessage }}
            name={'hasPermanentResidency'}
            radioOptions={residencyQuestions.hasPermanentResidency.options}
            radioGroupProps={{ className: 'mt-2' }}
          />
        </section>

        {noPermanentResidencyBoolean && (
          <section>
            <FormattedMessage id={residencyQuestions.remainingVisaMonths.labelKey} tagName={'h4'} />
            <FormNumberField
              name={'remainingVisaMonths'}
              textFieldProps={{
                InputProps: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <FormattedMessage id={residencyQuestions.remainingVisaMonths.adornmentKey} />
                    </InputAdornment>
                  ),
                },
              }}
              rules={{ required: requiredFieldErrorMessage }}
            />
          </section>
        )}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={!formState.hasPermanentResidency}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
